import { accountsStoreName } from '@hwm/ui-lib';
import { handleFulfilledAction, handleMultipleLoadingActions, BaseAccountStoreModel } from '@hwm/ui-lib';
import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { actionInterceptor } from '../middleware/actionsInterceptorMiddleware';
import { accountsThunks } from './accounts.thunks';
import { userProfileThunks } from './user-profile.thunks';
import { AccountModel, UserMainAccountModel } from '@api-model/account';

export interface AccountStoreState extends BaseAccountStoreModel<AccountModel, UserMainAccountModel> {}

export const accountsAdapter = createEntityAdapter({
  selectId: (acc: AccountModel) => acc?.id || acc?.['accountId'],
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialState: AccountStoreState = {
  error: null,
  hierarchy: [],
  loadingPending: 0,
  selectedAccount: null,
  subaccounts: accountsAdapter.getInitialState(),
  userRole: '',
  userMainAccount: null,
  includeArchived: false,
};

const slice = createSlice({
  name: accountsStoreName,
  initialState,
  reducers: {
    setSelectedAccount(state, action: PayloadAction<{ account: AccountModel }>) {
      state.selectedAccount = action.payload.account;
    },
    setHierarchy(state, action: PayloadAction<{ hierarchy: AccountModel[] }>) {
      state.hierarchy = action.payload.hierarchy;
    },
    accountsReceived(state, action: PayloadAction<{ accounts: AccountModel[] }>) {
      accountsAdapter.setAll(state.subaccounts, action.payload.accounts);
    },
    //
    setUserMainAccount: (state, { payload }: { payload: UserMainAccountModel }) => {
      state.userMainAccount = payload;
    },
    setUserRole: (state, { payload }: { payload: string }) => {
      state.userRole = payload;
    },
    setIncludeArchived: (state, { payload }: { payload: boolean }) => {
      state.includeArchived = payload;
    },
  },
  extraReducers: (builder) => {
    handleMultipleLoadingActions([accountsThunks.fetchSubaccounts], builder);
    handleFulfilledAction(accountsThunks.fetchSubaccounts, builder, (state, payload) => {
      accountsAdapter.setAll(state.subaccounts, payload);
    });
  },
});

export const accountsReducer = {
  slice,
};

actionInterceptor.addActionsCallback<typeof accountsReducer.slice.actions.setSelectedAccount>({
  actionTypes: [accountsReducer.slice.actions.setSelectedAccount.type],
  callback: ({ action, dispatch }) => {
    const { payload } = action;

    const accountId = (payload?.account?.id || payload?.account?.accountId)?.toString();

    if (!accountId) return;

    dispatch(accountsThunks.fetchSubaccounts({ accountId }));
  },
});

actionInterceptor.addActionsCallback<typeof userProfileThunks.getUserProfile.fulfilled>({
  actionTypes: [userProfileThunks.getUserProfile.fulfilled.type],
  callback: ({ dispatch, action }) => {
    dispatch(slice.actions.setUserMainAccount(action?.payload ?? null));
    dispatch(
      slice.actions.setSelectedAccount({
        account: action.payload,
      })
    );
    dispatch(
      slice.actions.setHierarchy({
        hierarchy: [action.payload],
      })
    );
  },
});
